import { css } from "@linaria/core";
import cx from "classnames";
import React, {
  createContext,
  memo,
  useContext,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  ArrowDownLine,
  ArrowLeftLine,
  ArrowRightLine,
  ArrowUpLine,
  DeleteBinLine,
  FileCopyLine,
  InsertColumnLeft,
  InsertColumnRight,
  InsertRowBottom,
  InsertRowTop,
} from "../../../../../../ui/IconV2/react";
import { colors, spacings } from "../../../../../../ui/Theme";
import TooltipV2 from "@ui/TooltipV2";
import t from "../../../../../../i18n";

export type LayoutItem = { id: string; x: number; y: number; w: number; h: number };

const MAX_GRID_COLUMN = 6;
const MAX_ROW_COUNT = 3;

const GridContext = createContext<{
  layout: LayoutItem[];
  onLayoutChange?: (layout: LayoutItem[]) => void;
  selectedItem: LayoutItem | null;
  setSelectedItem?: React.Dispatch<React.SetStateAction<LayoutItem | null>>;
}>({
  layout: [],
  selectedItem: null,
});

export const Grid = memo(
  ({
    layout,
    children,
    onLayoutChange,
  }: {
    layout: LayoutItem[];
    children: (item: LayoutItem) => ReactNode;
    onLayoutChange?: (layout: LayoutItem[]) => void;
  }) => {
    const [selectedItem, setSelectedItem] = useState<LayoutItem | null>(null);
    const editable = Boolean(onLayoutChange);
    return (
      <GridContext.Provider value={{ layout, selectedItem, onLayoutChange, setSelectedItem }}>
        <div className={grid}>
          {layout.map(item => (
            <div
              key={item.id}
              className={cx(gridItem, {
                editable,
                selected: editable && selectedItem?.id === item.id,
              })}
              onClick={() => editable && setSelectedItem(item)}
              style={
                {
                  gridColumn: `${item.x} / span ${item.w}`,
                  gridRow: `${item.y} / span ${item.h}`,
                } as CSSProperties
              }
            >
              {children(item)}
            </div>
          ))}
        </div>
      </GridContext.Provider>
    );
  },
);

export const GridItemAction = memo(
  ({ item, children }: React.PropsWithChildren<{ item: LayoutItem }>) => {
    const { selectedItem } = useContext(GridContext);

    return (
      <div
        className={cx(gridItemAction, { selected: selectedItem?.id === item.id })}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    );
  },
);

export const GridItemActionSeparator = () => <div className={gridItemActionSeparator} />;

type Direction = "left" | "up" | "right" | "down";

const directionToAxis: { [key in Direction]: { axis: "x" | "y"; increment: -1 | 1 } } = {
  left: { axis: "x", increment: -1 },
  right: { axis: "x", increment: 1 },
  up: { axis: "y", increment: -1 },
  down: { axis: "y", increment: 1 },
};

export const MoveGridItem = ({
  direction,
  source,
}: {
  direction: Direction;
  source: LayoutItem;
}) => {
  const { layout, selectedItem, onLayoutChange } = useContext(GridContext);
  if (!selectedItem) return null;
  const ys = layout.map(({ y }) => y);
  const xs = layout.map(({ x }) => x);
  const firstRow = direction === "up" && Math.min(...ys) >= source.y;
  const lastRow = direction === "down" && Math.max(...ys) <= source.y;
  const lastColumn = direction === "left" && Math.min(...xs) >= source.x;
  const firstColumn = direction === "right" && Math.max(...xs) <= source.x;
  const row = layout.filter(item => item.y === source.y);
  const severalItemsOnSameRow = (direction === "up" || direction === "down") && row.length > 1;

  return (
    <TooltipV2 overlay={t("project.dashboard.actions.move_grid_item", direction)}>
      <button
        disabled={firstRow || lastRow || lastColumn || firstColumn || severalItemsOnSameRow}
        onClick={() => {
          const { newLayout } = moveItem(layout, source, direction);
          onLayoutChange?.(newLayout);
        }}
      >
        {direction === "left" && <ArrowLeftLine size="s" />}
        {direction === "right" && <ArrowRightLine size="s" />}
        {direction === "up" && <ArrowUpLine size="s" />}
        {direction === "down" && <ArrowDownLine size="s" />}
      </button>
    </TooltipV2>
  );
};

export const InsertGridItem = ({
  direction,
  source,
}: {
  direction: Direction;
  source: LayoutItem;
}) => {
  const { layout, onLayoutChange, setSelectedItem } = useContext(GridContext);
  const row = layout.filter(item => item.y === source.y);
  const disableHorizontalInsertion =
    (direction === "left" || direction === "right") && row.length === 3;
  return (
    <TooltipV2 overlay={t("project.dashboard.actions.insert_grid_item", direction)}>
      <button
        disabled={disableHorizontalInsertion}
        onClick={() => {
          const { newLayout, newItem } = insertItem(layout, source, direction);
          setSelectedItem?.(newItem);
          onLayoutChange?.(newLayout);
        }}
      >
        {direction === "left" && <InsertColumnLeft size="s" />}
        {direction === "right" && <InsertColumnRight size="s" />}
        {direction === "up" && <InsertRowTop size="s" />}
        {direction === "down" && <InsertRowBottom size="s" />}
      </button>
    </TooltipV2>
  );
};

export const CopyGridItem = ({
  source,
  onCopyLayoutItem,
}: {
  source: LayoutItem;
  onCopyLayoutItem?: (toLayoutItem: LayoutItem, fromLayoutItem: LayoutItem) => void;
}) => {
  const { layout, onLayoutChange, setSelectedItem } = useContext(GridContext);

  return (
    <TooltipV2 overlay={t("project.dashboard.actions.duplicate")}>
      <button
        onClick={() => {
          const { newLayout, newItem } = insertItem(layout, source, "down");
          onCopyLayoutItem?.(newItem, source);
          onLayoutChange?.(newLayout);
          setSelectedItem?.(newItem);
        }}
      >
        <FileCopyLine size="s" />
      </button>
    </TooltipV2>
  );
};

export const RemoveGridItem = ({
  source,
  onRemoveLayoutItem,
}: {
  source: LayoutItem;
  onRemoveLayoutItem?: (layoutItem: LayoutItem) => void;
}) => {
  const { layout, onLayoutChange, setSelectedItem } = useContext(GridContext);

  return (
    <TooltipV2 overlay={t("project.dashboard.actions.delete")}>
      <button
        onClick={() => {
          const { newLayout, oldItem } = removeItem(layout, source);
          onRemoveLayoutItem?.(oldItem);
          onLayoutChange?.(newLayout);
          setSelectedItem?.(null);
        }}
      >
        <DeleteBinLine size="s" />
      </button>
    </TooltipV2>
  );
};

export function consolidate(layout: LayoutItem[]) {
  const rows = Object.values(
    layout
      .sort((a, b) => a.y - b.y)
      .reduce<{ [key: number]: LayoutItem[] }>((acc, item) => {
        acc[item.y] ||= [];
        acc[item.y]?.push(item);
        return acc;
      }, {}),
  );

  return rows.flatMap((row, i) =>
    row
      .sort((a, b) => a.x - b.x)
      .map((item, j, arr) => {
        const split = Math.max(1, (j * MAX_GRID_COLUMN) / arr.length + 1);
        return {
          ...item,
          x: split,
          w: MAX_GRID_COLUMN / row.length,
          y: i + 1,
        };
      }),
  );
}

function moveItem(layout: LayoutItem[], source: LayoutItem, dir: Direction) {
  const { axis, increment: inc } = directionToAxis[dir];
  const newPosition = source[axis] + inc * (axis === "x" ? source.w : 1);
  const nextSiblings = layout.filter(item => {
    const match = item[axis] === newPosition && item.id !== source.id;
    const xRule = axis === "x" && item.y === source.y;
    const yRule =
      axis === "y" && source.x <= item.x && item.x < source.x + source.w && item.w <= source.w;
    return match && (xRule || yRule);
  });
  if (
    nextSiblings.length === 0 ||
    nextSiblings.reduce<number>((acc, item) => acc + item.w, 0) < source.w
  )
    return { newLayout: layout };

  const newLayout = layout.map(item => {
    if (nextSiblings.includes(item)) return { ...item, [axis]: source[axis] };
    if (item.id === source.id) return { ...item, [axis]: newPosition };
    return item;
  });
  return { newLayout };
}

function insertItem(layout: LayoutItem[], source: LayoutItem, dir: Direction) {
  const { axis, increment: inc } = directionToAxis[dir];
  const newPosition = source[axis] + inc;
  const newItem = { ...source, id: crypto.randomUUID(), [axis]: newPosition };
  if (axis === "x" && layout.filter(item => item.y === source.y).length === MAX_ROW_COUNT) {
    return { newLayout: layout, newItem: source };
  }

  const newLayout = layout.map(item => {
    if (item[axis] > source[axis] && item.id !== newItem.id)
      return { ...item, [axis]: item[axis] + 1 };
    if (item[axis] < source[axis] && item.id !== newItem.id)
      return { ...item, [axis]: item[axis] - 1 };
    return item;
  });
  newLayout.push(newItem);
  return { newLayout: consolidate(newLayout), newItem };
}

function removeItem(layout: LayoutItem[], source: LayoutItem) {
  const newLayout = layout.filter(item => item.id !== source.id);
  return { newLayout: consolidate(newLayout), oldItem: source };
}

const grid = css`
  display: grid;
  gap: ${spacings.m};
  grid-auto-columns: 1fr;
`;

const gridItem = css`
  position: relative;

  &.editable {
    outline: 1px dashed ${colors.titanium300};
    border-radius: 4px;
    padding: ${spacings.xs};
    transition: outline 150ms ease-out;
  }

  &.editable:not(.selected):hover {
    outline-color: ${colors.lightNitrogen500_MainFade075};
  }

  &.selected {
    outline-width: 2px;
    outline-style: solid;
    outline-color: ${colors.lightNitrogen500_Main};
    z-index: 1;
  }
`;

const gridItemAction = css`
  background-color: ${colors.titanium800};
  border-radius: 4px;
  display: flex;
  gap: ${spacings.xxs};
  padding: ${spacings.xxs};
  position: absolute;
  top: -4px;
  left: -2px;
  transform: translateY(-125%);
  width: max-content;
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 200ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 200ms cubic-bezier(0.4, 0, 0.2, 1);

  &.selected {
    pointer-events: auto;
    opacity: 1;
    transform: translateY(-100%);
  }

  button {
    background: none;
    border: none;
    color: ${colors.hydrogen};
    margin: 0;
    padding: 0;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    svg {
      display: block;
    }
  }
`;

const gridItemActionSeparator = css`
  background-color: ${colors.hydrogen};
  height: 12px;
  opacity: 0.3;
  width: 1px;
`;
